import { lazy, type FC } from 'react';
import * as translator from '../../../utils/translator';
import type { StripeInitData } from '../paymentTypes';
import type { CheckoutFormProps, CheckoutProviderAdapter } from './types';

const loadStripeForm = () => import('../CheckoutForms/StripeCheckoutForm');

const StripeSectionForm = lazy(async () => {
  const { default: StripeCheckoutForm } = await loadStripeForm();
  const Form: FC<CheckoutFormProps<StripeInitData>> = ({ initData, variant, ...props }) => (
    <StripeCheckoutForm stripeInitData={initData} {...props} />
  );
  return { default: Form };
});

// Stripe's translated message for a code / decline code, else its own message, else the default.
const stripeMessage = (language: string, stripeError: { code?: string; decline_code?: string; message?: string }, defaultKey: string): string => {
  const defaultMsg = translator.translate(language, 'payment', defaultKey);
  let errorMsg = defaultMsg;
  if (stripeError.code) {
    const translatedByCode = translator.translate(language, 'payment', `stripe/errorCode/${stripeError.code}`);
    if (translatedByCode) errorMsg = translatedByCode;
  }
  if (stripeError.decline_code) {
    const translatedByDeclineCode = translator.translate(language, 'payment', `stripe/declineCode/${stripeError.decline_code}`);
    if (translatedByDeclineCode) errorMsg = translatedByDeclineCode;
  }
  if (errorMsg === defaultMsg && stripeError.message) {
    errorMsg = stripeError.message;
  }
  return errorMsg;
};

// stripe and stripe_gb share the adapter; the code is the PSP the section pays through.
export const createStripeAdapter = (code: 'stripe' | 'stripe_gb'): CheckoutProviderAdapter<StripeInitData> => ({
  code,

  buildInitData: (dto, { isSubscription }) => ({
    gatewayResult: dto.gatewayResult,
    stripePublicKey: dto.publicKey,
    customerSessionSecret: dto.gatewayResult.customerSessionSecret,
    isSubscription,
    customerData: dto.customerData,
    orderAddresses: dto.orderAddresses,
  }),

  applyAllowList: (init, native) => ({
    ...init,
    gatewayResult: { ...init.gatewayResult, paymentMethods: (init.gatewayResult?.paymentMethods ?? []).filter((method: string) => native.includes(method)) },
  }),

  loadSdk: loadStripeForm,
  Form: StripeSectionForm,

  interpretResult: (result, language) => ({
    message: result.stripeError
      ? stripeMessage(language, result.stripeError, `${code}/refusalCode/DEFAULT`)
      : translator.translate(language, 'payment', `${code}/refusalCode/DEFAULT`),
  }),

  interpretError: (errorResponse, language) => {
    const stripeError = errorResponse?.stripeError;
    if (!stripeError) return undefined;
    return {
      message: stripeMessage(language, stripeError, 'stripe/refusalCode/DEFAULT'),
      reason: `${code}: ${stripeError.code || stripeError.decline_code}`,
    };
  },

  // The PaymentIntent is read from the attempt row the return URL names (tx).
  readRedirectReturn: () => ({}),

  contextOverrides: () => ({ paymentServiceProvider: code, checkoutProvider: null }),
});
